import React, { useEffect } from 'react';
import { AlertTriangle, Trash2, X } from 'lucide-react';

const DeleteModal = ({ isOpen, onClose, onConfirm, isLoading }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
            document.documentElement.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
            document.documentElement.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !isLoading && onClose) {
            onClose();
        }
    };

    return (
        <div
            onClick={handleBackdropClick}
            className="fixed inset-0 z-[999] flex items-center justify-center p-3.5 sm:p-4 bg-black/40 backdrop-blur-md animate-in fade-in duration-300"
        >
            {/* Transparent Glassmorphism Modal Container */}
            <div className="w-full max-w-[340px] xs:max-w-sm sm:max-w-md bg-[#0c0c18] backdrop-blur-2xl border border-white/15 rounded-3xl p-5 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_30px_rgba(255,0,122,0.15)] relative overflow-hidden animate-in zoom-in-95 duration-300">

                {/* Neon Top Line */}
                <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-blue" />

                {/* Decorative background glow */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-cyber-pink/15 rounded-full blur-[60px] pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-cyber-blue/15 rounded-full blur-[60px] pointer-events-none" />

                {/* Close Button */}
                <button
                    onClick={onClose}
                    disabled={isLoading}
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all disabled:opacity-40 active:scale-95 cursor-pointer"
                    aria-label="Close modal"
                >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>

                <div className="text-center space-y-4 sm:space-y-6 pt-2 sm:pt-4">
                    {/* Icon Header */}
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-cyber-pink/10 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto border border-cyber-pink/20 relative group flex-shrink-0">
                        <div className="absolute inset-0 bg-cyber-pink/20 rounded-2xl sm:rounded-3xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity" />
                        <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10 text-cyber-pink relative z-10" />
                    </div>

                    <div className="space-y-1.5 sm:space-y-2">
                        <h3 className="text-lg sm:text-2xl font-cyber font-black text-white uppercase tracking-tight">
                            Delete this record?
                        </h3>
                        <p className="text-gray-400 text-xs sm:text-sm leading-relaxed font-sans px-1">
                            This will <span className="text-cyber-pink font-semibold">hide</span> this record from all views. It will still be saved in the database.
                        </p>
                    </div>

                    <div className="flex flex-col-reverse xs:flex-row gap-2.5 sm:gap-4 pt-2 sm:pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className="w-full xs:flex-1 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all disabled:opacity-50 active:scale-95 cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isLoading}
                            className="w-full xs:flex-1 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-cyber-pink/20 border border-cyber-pink/40 text-cyber-pink font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-pink/30 shadow-[0_0_20px_rgba(255,0,122,0.15)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 cursor-pointer"
                        >
                            {isLoading ? (
                                <span className="w-4 h-4 border-2 border-cyber-pink/40 border-t-cyber-pink rounded-full animate-spin" />
                            ) : (
                                <>
                                    <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="whitespace-nowrap">Yes, Delete</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteModal;
